'use client';

import { useQuery } from 'convex-helpers/react/cache/hooks';
import { api } from 'convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import {
  createCellSlotsMap,
  createGetSlotsForCell,
  getPlantCounts,
} from '@modules/garden-planner/business_logic';
import {
  getFreeCellCount,
  groupPlantCountsByCategory,
  hasPlantsInGarden,
} from '@modules/garden-planner/garden_summary/business_logic';
import { GardenSummary } from '@modules/garden-planner/views/garden_summary_view';
import { GardenGridReadOnlyContainer } from '@modules/shared-garden/containers/garden_grid_read_only_container';
import { GardenGridReadOnly } from '@modules/shared-garden/views/garden_grid_read_only_view';
import { ComponentType, type ReactNode, useMemo } from 'react';

export interface SharedGardenProps {
  isLoading: boolean;
  gardenName: string | null;
  summary: ReactNode;
  grid: ReactNode;
}

interface SharedGardenContainerProps {
  View: ComponentType<SharedGardenProps>;
  gardenId: Id<'gardens'>;
}

export function SharedGardenContainer({ View, gardenId }: SharedGardenContainerProps) {
  const garden = useQuery(api.gardens.queries.getSharedGarden, { gardenId });
  const plantsQuery = useQuery(api.plants.queries.getAllPlants, {});

  const isLoading = garden === undefined || plantsQuery === undefined;

  const getSlotsForCell = useMemo(() => {
    const map = createCellSlotsMap(garden?.cellSettings);
    return createGetSlotsForCell(map);
  }, [garden?.cellSettings]);

  if (!isLoading && !garden) {
    return null;
  }

  const plants = plantsQuery ?? [];
  const plantCounts = !isLoading && garden ? getPlantCounts(plants, garden.plants) : {};
  const plantGroups = groupPlantCountsByCategory(plantCounts, plants);

  const summary =
    !isLoading && garden ? (
      <GardenSummary
        plantGroups={plantGroups}
        hasPlantsInGarden={hasPlantsInGarden(plantCounts)}
        totalPlanted={garden.plants.length}
        freeSpots={getFreeCellCount(garden)}
      />
    ) : null;

  const grid =
    !isLoading && garden ? (
      <GardenGridReadOnlyContainer
        View={GardenGridReadOnly}
        rows={garden.rows}
        columns={garden.columns}
        plants={garden.plants}
        availableCells={garden.availableCells ?? []}
        getSlotsForCell={getSlotsForCell}
        plantsList={plants}
      />
    ) : null;

  return (
    <View isLoading={isLoading} gardenName={garden?.name ?? null} summary={summary} grid={grid} />
  );
}
